import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dataService } from '../../services/dataService';
import { getAgeBand, getAgeBandLabel } from '../../lib/domain-data';
import { CONSENT_TITLE, researchConsentLabel } from '../../lib/consent';
import { AvatarBadge } from '../components/AvatarPicker';
import FamilyDataSection from '../components/FamilyDataSection';
import ReminderSettings from '../components/ReminderSettings';
import {
  ProfileFields,
  ProfileFormValue,
  emptyProfileForm,
  isProfileComplete,
  profileFormFromProfile,
  resolveProfileForm
} from '../components/ProfileFields';

/**
 * FR-02 — the patient's profile editor (SoW Bagian 2.2).
 *
 * Until this page existed the profile was write-once: filled at registration
 * and never shown again. It also hosts the FR-03 family data, which had a
 * route of its own only because this page did not exist yet.
 */
export default function ProfilePage() {
  const { profile, user, refreshProfile } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState<ProfileFormValue>(emptyProfileForm);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Seeded from the stored profile, and re-seeded only when the stored FR-02
  // fields change. AuthContext revalidates the profile in the background and
  // hands out a new object each time, and the family section's save touches
  // updated_at — following either would throw away whatever the parent was
  // halfway through typing here.
  const storedForm = profileFormFromProfile(profile);
  const storedKey = JSON.stringify([profile?.id, storedForm]);
  useEffect(() => {
    setForm(storedForm);
  }, [storedKey]);

  const handleSave = async (event: FormEvent) => {
    event.preventDefault();
    if (!user) {
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const { details, place, doctor } = await resolveProfileForm(form);
      await dataService.updateProfileDetails(user.id, details, place, doctor);
      // Without this the AuthContext cache serves the pre-edit profile for up
      // to its TTL — and the birth date is what picks the injection-site band.
      await refreshProfile();
      setNotice('Profil tersimpan.');
    } catch (err: any) {
      setError(err?.message || 'Gagal menyimpan profil.');
    } finally {
      setBusy(false);
    }
  };

  const complete = isProfileComplete(profile);
  const band = getAgeBand(profile?.birth_date);

  return (
    <div className="screen min-h-[100dvh] h-full">
      <section className="screen-card" aria-labelledby="profile-title">
        <div className="profile-heading">
          <AvatarBadge avatarId={profile?.avatar_id} size={64} />
          <div>
            <div id="profile-title" className="section-title">
              Profil
            </div>
            <div className="subtitle">
              {band ? `Kelompok usia ${getAgeBandLabel(band)}` : 'Lengkapi tanggal lahir'}
            </div>
          </div>
        </div>

        {!complete ? (
          <div className="profile-nudge" role="status">
            Profil belum lengkap. Semua data wajib diisi, kecuali nomor rekam medis.
          </div>
        ) : null}

        <form onSubmit={handleSave}>
          <ProfileFields value={form} onChange={setForm} disabled={busy} idPrefix="profile" />

          {error ? <div className="target-band-error">{error}</div> : null}
          {notice ? <div className="target-band-preview">{notice}</div> : null}

          <div className="target-actions">
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? 'Menyimpan...' : 'Simpan profil'}
            </button>
          </div>
        </form>
      </section>

      <FamilyDataSection />

      {user ? <ReminderSettings userId={user.id} /> : null}

      <section className="screen-card" aria-labelledby="consent-summary-title">
        <div id="consent-summary-title" className="section-title">
          {CONSENT_TITLE}
        </div>
        <div className="subtitle">
          Jawaban: <strong>{researchConsentLabel(profile)}</strong>
        </div>
        <div className="target-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate('/consent', { state: { from: '/profile' } })}
          >
            Ubah pilihan
          </button>
        </div>
      </section>

      <div className="next-actions" style={{ marginTop: 16 }}>
        <button type="button" className="btn-secondary" onClick={() => navigate('/')}>
          Kembali
        </button>
      </div>
    </div>
  );
}
